import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/feature/Layout';
import ProtectedRoute from '../../components/feature/ProtectedRoute';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, isSameMonth, isSameDay, startOfWeek, endOfWeek } from 'date-fns';
import { ko } from 'date-fns/locale';

interface Company {
  id: string;
  company_name: string;
  current_status: string;
  visit1_date: string | null;
  first_visit_time: string | null;
  visit2_date: string | null;
  second_visit_time: string | null;
  visit3_date: string | null;
  third_visit_time: string | null;
  poc_start_date: string | null;
  contract_date: string | null;
  sales_person_name: string;
  region: string | null;
}

interface DeveloperSchedule {
  id: string;
  schedule_date: string;
  schedule_time: string;
  developer_name: string;
  location: string | null;
  company_name: string | null;
}

const MainPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [companies, setCompanies] = useState<Company[]>([]);
  const [developerSchedules, setDeveloperSchedules] = useState<DeveloperSchedule[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'sales' | 'developer'>('sales');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [editingSchedule, setEditingSchedule] = useState<DeveloperSchedule | null>(null);
  const [newSchedule, setNewSchedule] = useState({
    time: '',
    developer_name: '',
    company_name: '',
    location: ''
  });
  const [editSchedule, setEditSchedule] = useState({
    time: '',
    developer_name: '',
    company_name: '',
    location: ''
  });

  const menuItems = [
    { title: '기업 목록', icon: 'ri-building-line', path: '/company-list' },
    { title: '기업 등록', icon: 'ri-add-circle-line', path: '/company-register' },
    { title: 'Call 관리', icon: 'ri-phone-line', path: '/call-management' },
    { title: 'DB 파일 업로드', icon: 'ri-upload-cloud-line', path: '/data-upload' },
    { title: '사용자 관리', icon: 'ri-user-settings-line', path: '/user-management', adminOnly: true },
  ];

  useEffect(() => {
    fetchCompanies();
    fetchDeveloperSchedules();
  }, [user]);

  const fetchCompanies = async () => {
    try {
      let query = supabase.from('companies').select('*');

      // 영업자인 경우 자신의 데이터만 조회 (이름으로 필터링)
      if (user?.role === '영업자') {
        console.log('Filtering by sales_person_name:', user.name);
        query = query.eq('sales_person_name', user.name);
      }

      const { data, error } = await query;

      if (error) {
        console.error('Supabase error:', error);
        throw error;
      }

      console.log('Fetched companies:', data);
      console.log('Current user:', user);
      console.log('User role:', user?.role);
      console.log('User name:', user?.name);

      setCompanies(data || []);
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDeveloperSchedules = async () => {
    try {
      const { data, error } = await supabase
        .from('developer_schedules')
        .select('*')
        .order('schedule_date', { ascending: true });

      if (error) throw error;
      setDeveloperSchedules(data || []);
    } catch (error) {
      console.error('Error fetching developer schedules:', error);
    }
  };

  const handleAddSchedule = async () => {
    if (!selectedDate || !newSchedule.time || !newSchedule.developer_name) {
      alert('시간과 AI expert명은 필수 항목입니다.');
      return;
    }

    // 하루 최대 4개 체크
    const existingSchedules = getDeveloperSchedulesForDate(selectedDate);
    if (existingSchedules.length >= 4) {
      alert('하루에 최대 4개의 일정만 추가할 수 있습니다.');
      return;
    }

    try {
      const { error } = await supabase.from('developer_schedules').insert({
        schedule_date: selectedDate.toISOString(),
        schedule_time: newSchedule.time,
        developer_name: newSchedule.developer_name,
        company_name: newSchedule.company_name || null,
        location: newSchedule.location || null
      });

      if (error) throw error;

      setShowAddModal(false);
      setNewSchedule({ time: '', developer_name: '', company_name: '', location: '' });
      setSelectedDate(null);
      fetchDeveloperSchedules();
    } catch (error) {
      console.error('Error adding schedule:', error);
      alert('일정 추가에 실패했습니다.');
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    if (!confirm('이 일정을 삭제하시겠습니까?')) return;

    try {
      const { error } = await supabase
        .from('developer_schedules')
        .delete()
        .eq('id', scheduleId);

      if (error) throw error;
      fetchDeveloperSchedules();
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('일정 삭제에 실패했습니다.');
    }
  };

  const handleEditSchedule = async () => {
    if (!editingSchedule || !editSchedule.time || !editSchedule.developer_name) {
      alert('시간과 AI expert명은 필수 항목입니다.');
      return;
    }

    try {
      const { error } = await supabase
        .from('developer_schedules')
        .update({
          schedule_time: editSchedule.time,
          developer_name: editSchedule.developer_name,
          company_name: editSchedule.company_name || null,
          location: editSchedule.location || null
        })
        .eq('id', editingSchedule.id);

      if (error) throw error;

      setShowEditModal(false);
      setEditingSchedule(null);
      setEditSchedule({ time: '', developer_name: '', company_name: '', location: '' });
      fetchDeveloperSchedules();
      alert('일정이 수정되었습니다.');
    } catch (error) {
      console.error('Error updating schedule:', error);
      alert('일정 수정에 실패했습니다.');
    }
  };

  const handleTabChange = (tab: 'sales' | 'developer') => {
    // 모든 사용자가 AI expert 탭을 볼 수 있도록 변경
    setActiveTab(tab);
  };

  const openModal = (type: 'sales' | 'developer', date?: Date) => {
    // AI expert 일정 추가는 관리자와 AI expert만 가능
    if (type === 'developer' && user?.role === '영업자') {
      alert('AI expert 일정은 관리자와 AI expert만 추가할 수 있습니다.');
      return;
    }
    
    if (type === 'developer') {
      setSelectedDate(date || new Date());
      setShowAddModal(true);
    }
  };

  const openEditModal = (schedule: DeveloperSchedule) => {
    setEditingSchedule(schedule);
    setEditSchedule({
      time: schedule.schedule_time,
      developer_name: schedule.developer_name,
      company_name: schedule.company_name || '',
      location: schedule.location || ''
    });
    setShowEditModal(true);
  };

  const getEventsForDate = (date: Date) => {
    const events: { company: Company; type: string; time?: string }[] = [];

    companies.forEach((company) => {
      if (company.visit1_date && isSameDay(new Date(company.visit1_date), date)) {
        events.push({ 
          company, 
          type: '1차방문', 
          time: company.first_visit_time || undefined 
        });
      }
      if (company.visit2_date && isSameDay(new Date(company.visit2_date), date)) {
        events.push({ 
          company, 
          type: '2차방문', 
          time: company.second_visit_time || undefined 
        });
      }
      if (company.visit3_date && isSameDay(new Date(company.visit3_date), date)) {
        events.push({ 
          company, 
          type: '3차방문', 
          time: company.third_visit_time || undefined 
        });
      }
      if (company.poc_start_date && isSameDay(new Date(company.poc_start_date), date)) {
        events.push({ company, type: 'POC 시작' });
      }
      if (company.contract_date && isSameDay(new Date(company.contract_date), date)) {
        events.push({ company, type: '계약체결' });
      }
    });

    return events;
  };

  const getDeveloperSchedulesForDate = (date: Date) => {
    return developerSchedules.filter(schedule => 
      isSameDay(new Date(schedule.schedule_date), date)
    );
  };

  // 영업자 일정 개수 계산
  const salesSchedulesCount = companies.reduce((count, company) => {
    let scheduleCount = 0;
    if (company.visit1_date) scheduleCount++;
    if (company.visit2_date) scheduleCount++;
    if (company.visit3_date) scheduleCount++;
    if (company.poc_start_date) scheduleCount++;
    if (company.contract_date) scheduleCount++;
    return count + scheduleCount;
  }, 0);

  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const calendarStart = startOfWeek(monthStart, { weekStartsOn: 0 });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn: 0 });
  const calendarDays = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = ['일', '월', '화', '수', '목', '금', '토'];

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const getStatusColor = (type: string) => {
    switch (type) {
      case '1차방문':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case '2차방문':
        return 'bg-green-100 text-green-700 border-green-200';
      case '3차방문':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'POC 시작':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case '계약체결':
        return 'bg-red-100 text-red-700 border-red-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  if (loading) {
    return (
      <ProtectedRoute>
        <Layout>
          <div className="flex items-center justify-center h-96">
            <div className="flex flex-col items-center space-y-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
              <div className="text-lg text-gray-600">데이터를 불러오는 중...</div>
            </div>
          </div>
        </Layout>
      </ProtectedRoute>
    );
  }

  return (
    <ProtectedRoute>
      <Layout>
        <div className="bg-white rounded-lg shadow-sm p-4 sm:p-6">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold text-gray-900">일정관리</h1>
            <div className="flex items-center space-x-4">
              <button
                onClick={previousMonth}
                className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
              >
                <i className="ri-arrow-left-s-line text-xl"></i>
              </button>
              <span className="text-lg font-semibold text-gray-900 min-w-[120px] text-center">
                {format(currentDate, 'yyyy년 M월', { locale: ko })}
              </span>
              <button
                onClick={nextMonth}
                className="p-2 hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
              >
                <i className="ri-arrow-right-s-line text-xl"></i>
              </button>
            </div>
          </div>

          {/* 데이터 없음 안내 */}
          {companies.length === 0 && developerSchedules.length === 0 && user?.role !== '영업자' && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6">
              <div className="flex items-start space-x-3">
                <i className="ri-information-line text-2xl text-blue-600 mt-1"></i>
                <div>
                  <h3 className="text-lg font-semibold text-blue-900 mb-2">등록된 일정이 없습니다</h3>
                  <div className="text-sm text-blue-700 space-y-1">
                    <p>• 기업 목록에서 기업을 등록하고 방문 일정을 추가해보세요</p>
                    <p>• AI expert 일정 탭에서 개발 일정을 추가할 수 있습니다</p>
                  </div>
                  <button
                    onClick={() => navigate('/company')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    기업 목록으로 이동
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 탭 버튼 */}
          <div className="flex space-x-2 mb-6">
            <button
              onClick={() => handleTabChange('sales')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'sales'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              영업자 일정 ({salesSchedulesCount})
            </button>
            <button
              onClick={() => handleTabChange('developer')}
              className={`px-6 py-3 rounded-lg font-medium transition-colors cursor-pointer whitespace-nowrap ${
                activeTab === 'developer'
                  ? 'bg-indigo-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              AI expert 일정 ({developerSchedules.length})
            </button>
          </div>

          {/* 범례 및 일정 추가 버튼 */}
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center">
                <div className={`w-4 h-4 rounded mr-2 ${activeTab === 'sales' ? 'bg-indigo-500' : 'bg-purple-500'}`}></div>
                <span className="text-gray-700">{activeTab === 'sales' ? '영업자 일정' : 'AI expert 일정'}</span>
              </div>
            </div>
            {/* 영업자는 AI expert 일정 추가 버튼 숨김 */}
            {!(activeTab === 'developer' && user?.role === '영업자') && (
              <button
                onClick={() => openModal(activeTab)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-add-line mr-2"></i>
                일정 추가
              </button>
            )}
          </div>

          {/* Sales Calendar */}
          {activeTab === 'sales' && (
            <>
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {weekDays.map((day, index) => (
                  <div
                    key={day}
                    className={`text-center font-semibold text-sm py-2 ${
                      index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : 'text-gray-700'
                    }`}
                  >
                    {day}
                  </div>
                ))}

                {calendarDays.map((day, index) => {
                  const events = getEventsForDate(day);
                  const devSchedules = getDeveloperSchedulesForDate(day);
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const isToday = isSameDay(day, new Date());

                  return (
                    <div
                      key={day.toISOString()}
                      className={`min-h-[80px] sm:min-h-[120px] p-1 sm:p-2 border rounded-lg ${
                        isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                      } ${isToday ? 'border-indigo-500 border-2' : 'border-gray-200'}`}
                    >
                      <div
                        className={`text-xs sm:text-sm font-medium mb-1 ${
                          !isCurrentMonth
                            ? 'text-gray-400'
                            : index % 7 === 0
                            ? 'text-red-600'
                            : index % 7 === 6
                            ? 'text-blue-600'
                            : 'text-gray-700'
                        }`}
                      >
                        {format(day, 'd')}
                      </div>

                      <div className="space-y-1">
                        {events.map((event, eventIndex) => (
                          <button
                            key={eventIndex}
                            onClick={() => navigate(`/company/${event.company.id}`)}
                            className={`w-full text-left px-1 sm:px-2 py-1 rounded text-[10px] sm:text-xs border cursor-pointer hover:opacity-80 transition-opacity ${getStatusColor(
                              event.type
                            )}`}
                          >
                            <div className="font-medium truncate">{event.company.company_name}</div>
                            <div className="truncate">
                              {event.type}{event.company.region && `(${event.company.region})`} {event.time && `${event.time}`}
                            </div>
                            <div className="truncate text-[9px] sm:text-[11px] opacity-80">
                              담당: {event.company.sales_person_name}
                            </div>
                          </button>
                        ))}
                        
                        {devSchedules.length > 0 && (
                          <div className={`${events.length > 0 ? 'border-t border-gray-200 pt-1 mt-1' : ''}`}>
                            {devSchedules.map((schedule) => (
                              <div
                                key={schedule.id}
                                className="w-full text-left px-1 sm:px-2 py-1 rounded text-[10px] sm:text-xs bg-orange-50 text-orange-700 border border-orange-200 pointer-events-none"
                              >
                                <div className="font-medium">🔧 {schedule.schedule_time}</div>
                                <div className="truncate">{schedule.developer_name}</div>
                                {schedule.location && (
                                  <div className="truncate text-[9px] sm:text-[11px] opacity-80">
                                    📍 {schedule.location}
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 flex flex-wrap gap-3 sm:gap-4 text-xs sm:text-sm">
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded bg-blue-100 border border-blue-200 mr-2"></div>
                  <span className="text-gray-700">1차방문</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded bg-green-100 border border-green-200 mr-2"></div>
                  <span className="text-gray-700">2차방문</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded bg-yellow-100 border border-yellow-200 mr-2"></div>
                  <span className="text-gray-700">3차방문</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded bg-purple-100 border border-purple-200 mr-2"></div>
                  <span className="text-gray-700">POC 시작</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded bg-red-100 border border-red-200 mr-2"></div>
                  <span className="text-gray-700">계약체결</span>
                </div>
                <div className="flex items-center">
                  <div className="w-4 h-4 rounded bg-orange-50 border border-orange-200 mr-2"></div>
                  <span className="text-gray-700">AI expert 일정</span>
                </div>
              </div>

              {companies.length === 0 && user?.role === '영업자' && (
                <div className="mt-4 text-sm text-gray-600">
                  <p>💡 아직 담당 업체가 없습니다. AI expert 일정은 위 달력에서 확인할 수 있습니다.</p>
                </div>
              )}
            </>
          )}

          {/* Developer Calendar */}
          {activeTab === 'developer' && (
            <>
              <div className="grid grid-cols-7 gap-1 sm:gap-2">
                {weekDays.map((day, index) => (
                  <div
                    key={day}
                    className={`text-center font-semibold text-sm py-2 ${
                      index === 0 ? 'text-red-600' : index === 6 ? 'text-blue-600' : 'text-gray-700'
                    }`}
                  >
                    {day}
                  </div>
                ))}

                {calendarDays.map((day, index) => {
                  const devSchedules = getDeveloperSchedulesForDate(day);
                  const isCurrentMonth = isSameMonth(day, currentDate);
                  const isToday = isSameDay(day, new Date());
                  const canAddMore = devSchedules.length < 4;
                  // 영업자는 일정 추가 버튼 숨김
                  const canAddSchedule = user?.role !== '영업자';

                  return (
                    <div
                      key={day.toISOString()}
                      className={`min-h-[100px] sm:min-h-[140px] p-1 sm:p-2 border rounded-lg ${
                        isCurrentMonth ? 'bg-white' : 'bg-gray-50'
                      } ${isToday ? 'border-indigo-500 border-2' : 'border-gray-200'}`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div
                          className={`text-xs sm:text-sm font-medium ${
                            !isCurrentMonth
                              ? 'text-gray-400'
                              : index % 7 === 0
                              ? 'text-red-600'
                              : index % 7 === 6
                              ? 'text-blue-600'
                              : 'text-gray-700'
                          }`}
                        >
                          {format(day, 'd')}
                        </div>
                        {isCurrentMonth && canAddMore && canAddSchedule && (
                          <button
                            onClick={() => {
                              setSelectedDate(day);
                              setShowAddModal(true);
                            }}
                            className="w-5 h-5 flex items-center justify-center hover:bg-gray-100 rounded cursor-pointer transition-colors"
                          >
                            <i className="ri-add-line text-sm text-gray-600"></i>
                          </button>
                        )}
                        {isCurrentMonth && !canAddMore && (
                          <span className="text-[10px] text-gray-400">최대</span>
                        )}
                      </div>

                      <div className="space-y-1">
                        {devSchedules.map((schedule) => (
                          <div
                            key={schedule.id}
                            onClick={() => {
                              // 영업자는 수정 불가
                              if (user?.role !== '영업자') {
                                openEditModal(schedule);
                              }
                            }}
                            className={`w-full text-left px-1 sm:px-2 py-1 rounded text-[10px] sm:text-xs bg-orange-50 text-orange-700 border border-orange-200 group relative ${
                              user?.role !== '영업자' ? 'cursor-pointer hover:bg-orange-100' : ''
                            } transition-colors`}
                          >
                            <div className="font-medium">{schedule.schedule_time}</div>
                            <div className="truncate">{schedule.developer_name}</div>
                            {schedule.company_name && (user?.role === '관리자' || user?.role === 'AI expert') && (
                              <div className="truncate font-medium text-[9px] sm:text-[11px]">
                                🏢 {schedule.company_name}
                              </div>
                            )}
                            {schedule.location && (
                              <div className="truncate text-[9px] sm:text-[11px] opacity-80">
                                📍 {schedule.location}
                              </div>
                            )}
                            {/* 영업자는 삭제 버튼 숨김 */}
                            {user?.role !== '영업자' && (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteSchedule(schedule.id);
                                }}
                                className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center bg-red-500 text-white rounded opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                              >
                                <i className="ri-close-line text-xs"></i>
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="mt-6 text-sm text-gray-600">
                {user?.role !== '영업자' ? (
                  <>
                    <p>💡 날짜 칸의 + 버튼을 클릭하여 AI expert 일정을 추가할 수 있습니다. (하루 최대 4개)</p>
                    <p className="mt-1">💡 일정을 클릭하면 수정할 수 있고, 마우스를 올리면 삭제 버튼이 나타납니다.</p>
                  </>
                ) : (
                  <p>💡 AI expert 일정을 조회할 수 있습니다. 일정 추가/수정/삭제는 관리자와 AI expert만 가능합니다.</p>
                )}
                {developerSchedules.length === 0 && (
                  <p className="mt-2 text-indigo-600 font-medium">📌 아직 등록된 AI expert 일정이 없습니다. {user?.role !== '영업자' && '날짜를 선택하여 일정을 추가해보세요!'}</p>
                )}
              </div>
            </>
          )}
        </div>

        {/* Add Schedule Modal */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">AI expert 일정 추가</h2>
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewSchedule({ time: '', developer_name: '', company_name: '', location: '' });
                    setSelectedDate(null);
                  }}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  날짜
                </label>
                <input
                  type="text"
                  value={selectedDate ? format(selectedDate, 'yyyy년 M월 d일', { locale: ko }) : ''}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  시간 * (예: 14:30, 오후 2시 30분)
                </label>
                <input
                  type="text"
                  value={newSchedule.time}
                  onChange={(e) => setNewSchedule({ ...newSchedule, time: e.target.value })}
                  placeholder="시간을 입력하세요 (예: 14:30)"
                  maxLength={20}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI expert명 *
                </label>
                <input
                  type="text"
                  value={newSchedule.developer_name}
                  onChange={(e) => setNewSchedule({ ...newSchedule, developer_name: e.target.value })}
                  placeholder="AI expert 이름을 입력하세요"
                  maxLength={50}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  업체명
                </label>
                <input
                  type="text"
                  value={newSchedule.company_name}
                  onChange={(e) => setNewSchedule({ ...newSchedule, company_name: e.target.value })}
                  placeholder="업체명을 입력하세요"
                  maxLength={100}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">주소</label>
                <input
                  type="text"
                  value={newSchedule.location}
                  onChange={(e) => setNewSchedule({ ...newSchedule, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="예: 충남 공주"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowAddModal(false);
                    setNewSchedule({ time: '', developer_name: '', company_name: '', location: '' });
                    setSelectedDate(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  취소
                </button>
                <button
                  onClick={handleAddSchedule}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  추가
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Edit Schedule Modal */}
        {showEditModal && editingSchedule && (
          <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-bold text-gray-900">AI expert 일정 수정</h2>
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSchedule(null);
                    setEditSchedule({ time: '', developer_name: '', company_name: '', location: '' });
                  }}
                  className="w-8 h-8 flex items-center justify-center hover:bg-gray-100 rounded-lg cursor-pointer transition-colors"
                >
                  <i className="ri-close-line text-xl text-gray-600"></i>
                </button>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  날짜
                </label>
                <input
                  type="text"
                  value={format(new Date(editingSchedule.schedule_date), 'yyyy년 M월 d일', { locale: ko })}
                  disabled
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 text-gray-700"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  시간 * (예: 14:30, 오후 2시 30분)
                </label>
                <input
                  type="text"
                  value={editSchedule.time}
                  onChange={(e) => setEditSchedule({ ...editSchedule, time: e.target.value })}
                  placeholder="시간을 입력하세요 (예: 14:30)"
                  maxLength={20}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  AI expert명 *
                </label>
                <input
                  type="text"
                  value={editSchedule.developer_name}
                  onChange={(e) => setEditSchedule({ ...editSchedule, developer_name: e.target.value })}
                  placeholder="AI expert 이름을 입력하세요"
                  maxLength={50}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  업체명
                </label>
                <input
                  type="text"
                  value={editSchedule.company_name}
                  onChange={(e) => setEditSchedule({ ...editSchedule, company_name: e.target.value })}
                  placeholder="업체명을 입력하세요"
                  maxLength={100}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-sm"
                />
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-1.5">주소</label>
                <input
                  type="text"
                  value={editSchedule.location}
                  onChange={(e) => setEditSchedule({ ...editSchedule, location: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 text-sm"
                  placeholder="예: 충남 공주"
                />
              </div>

              <div className="flex space-x-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setEditingSchedule(null);
                    setEditSchedule({ time: '', developer_name: '', company_name: '', location: '' });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                >
                  취소
                </button>
                <button
                  onClick={handleEditSchedule}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors cursor-pointer whitespace-nowrap"
                >
                  저장
                </button>
              </div>
            </div>
          </div>
        )}
      </Layout>
    </ProtectedRoute>
  );
};

export default MainPage;
